import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../auth/useAuth";
import { SessionContext } from "./SessionContext";
import { jwtExpiresInMs } from "./jwt";
import { isSilentRefreshRoute } from "./routes";
import { useIdleTimer } from "./useIdleTimer";
import { useSystemIdleTimer } from "./useSystemIdleTimer";
import { useBroadcastChannel } from "./useBroadcastChannel";
import { refreshTokenRequest } from "../services/api/session";
import IdleModal from "./IdleModal";
import SessionExpiryModal from "./SessionExpiryModal";

const IDLE_MS = 20 * 60 * 1000;
const SYSTEM_IDLE_MS = 20 * 60 * 1000;
const COUNTDOWN_SECONDS = 30;
const REFRESH_LEAD_MS = 60 * 1000;
const MIN_TIMER_MS = 5 * 1000;
const CHANNEL_NAME = "cf_session_channel";
const SYSTEM_IDLE_CHANNEL_NAME = "cf_system_idle_channel";
const REFRESH_TOKEN_STORAGE_KEY = "refreshToken";
const SESSION_HANDLED_FLAG = "cf_session_handled_by_layout";

const REFRESH_EVENTS = {
  TOKEN_REFRESHED: "TOKEN_REFRESHED",
  LOGOUT: "LOGOUT",
  IDLE_WARNING: "IDLE_WARNING",
  IDLE_DECISION: "IDLE_DECISION",
};

const IDLE_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];

export const SessionProvider = ({ children }) => {
  const location = useLocation();
  const pathname = location.pathname;
  const silent = isSilentRefreshRoute(pathname);

  const { token, setToken, logout, isAuthenticated } = useAuth();

  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [showIdleModal, setShowIdleModal] = useState(false);
  const [idleModalSource, setIdleModalSource] = useState(null);
  const [idleWarningOrigin, setIdleWarningOrigin] = useState("self");
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  const [isTabVisible, setIsTabVisible] = useState(() =>
    typeof document !== "undefined" ? !document.hidden : true,
  );

  const refreshTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const inFlightRefreshRef = useRef(null);

  const hasResetOnFocusRef = useRef(false);
  const logoutFiredRef = useRef(false);

  const broadcastRef = useRef(() => {});

  const performLogoutRef = useRef(() => {});

  const { isIdle: isTabIdle, resetIdleTimer: resetTabIdle } = useIdleTimer({
    idleMs: IDLE_MS,
    events: IDLE_EVENTS,
  });

  const { isIdle: isSystemIdle, resetIdleTimer: resetSystemIdle } = useSystemIdleTimer({
    idleMs: SYSTEM_IDLE_MS,
    events: IDLE_EVENTS,
    channelName: SYSTEM_IDLE_CHANNEL_NAME,
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isSystemIdle) return;
    if (showIdleModal) return;

    const screenOn = typeof document !== "undefined" &&
      document.visibilityState === "visible";

    if (screenOn) {
      setShowIdleModal(true);
      setIdleModalSource("system");
    } else {
      logoutFiredRef.current = true;
      performLogoutRef.current();
    }
  }, [isSystemIdle, isAuthenticated, showIdleModal]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (isSystemIdle) return;
    if (isTabIdle && !showIdleModal) {
      setShowIdleModal(true);
      setIdleModalSource("tab");
      setIdleWarningOrigin("self");
      broadcastRef.current({ type: REFRESH_EVENTS.IDLE_WARNING });
    }
  }, [isTabIdle, isSystemIdle, isAuthenticated, showIdleModal]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const handleVisible = () => {
      if (document.hidden) {
        setIsTabVisible(false);
      } else {
        setIsTabVisible(true);
      }
    };
    const handleWindowFocus = () => {
      setIsTabVisible(true);
    };

    document.addEventListener("visibilitychange", handleVisible);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisible);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, []);

  useEffect(() => {
    if (!isTabVisible) return;
    if (!showIdleModal) return;
    if (hasResetOnFocusRef.current) return;
    setCountdown(COUNTDOWN_SECONDS);
    hasResetOnFocusRef.current = true;
  }, [isTabVisible, showIdleModal]);

  useEffect(() => {
    if (showIdleModal) {
      setCountdown(COUNTDOWN_SECONDS);
    }
  }, [showIdleModal]);

  useEffect(() => {
    if (showRefreshModal) {
      setCountdown(COUNTDOWN_SECONDS);
    }
  }, [showRefreshModal, showIdleModal]);

  useEffect(() => {
    if (!showRefreshModal && !showIdleModal) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return undefined;
    }

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((c) => Math.max(c - 1, 0));
      if (
        idleModalSource === "system" &&
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      ) {
        if (!logoutFiredRef.current) {
          logoutFiredRef.current = true;
          performLogoutRef.current();
          setShowIdleModal(false);
        }
      }
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [showRefreshModal, showIdleModal, idleModalSource]);

    useEffect(() => {
      if (countdown > 0) return;
      if (logoutFiredRef.current) return;
      if (!showRefreshModal && !showIdleModal) return;
      logoutFiredRef.current = true;
      if (showRefreshModal) {
        setShowRefreshModal(false);
      }
      if (showIdleModal) {
        setIdleModalSource(null);
        setIdleWarningOrigin("self");
        hasResetOnFocusRef.current = false;
        broadcastRef.current({
          type: REFRESH_EVENTS.IDLE_DECISION,
          action: "logout",
        });
        setShowIdleModal(false);
      }
      performLogoutRef.current();
    }, [countdown, showRefreshModal, showIdleModal]);

  const performRefresh = useCallback(async () => {
    if (inFlightRefreshRef.current) {
      return inFlightRefreshRef.current;
    }

    const promise = (async () => {
      const refreshTokenValue =
        localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
      if (!refreshTokenValue) {
        throw new Error("No refresh token available");
      }

      const result = await refreshTokenRequest({
        refreshToken: refreshTokenValue,
      });
      const newToken = result.token;
      const newRefresh = result.refreshToken || null;

      if (newRefresh) {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, newRefresh);
      }

      setToken(newToken);
      setShowRefreshModal(false);

      broadcastRef.current({
        type: REFRESH_EVENTS.TOKEN_REFRESHED,
        token: newToken,
        refreshToken: newRefresh || undefined,
      });

      return newToken;
    })();

    inFlightRefreshRef.current = promise;
    try {
      return await promise;
    } finally {
      inFlightRefreshRef.current = null;
    }
  }, [setToken]);

  const performLogout = useCallback(() => {
    broadcastRef.current({ type: REFRESH_EVENTS.LOGOUT });
    logout();
    if (typeof window !== "undefined") {
      window.location.href = window.location.origin;
    }
  }, [logout]);

  const { postMessage: broadcast } = useBroadcastChannel({
    channelName: CHANNEL_NAME,
    onMessage: (data) => {
      if (!data || typeof data !== "object") return;
      if (data.type === REFRESH_EVENTS.TOKEN_REFRESHED && data.token) {
        if (data.refreshToken) {
          try {
            localStorage.setItem(
              REFRESH_TOKEN_STORAGE_KEY,
              data.refreshToken,
            );
          } catch {
          void 0;
          }
        }
        setToken(data.token);
        setShowRefreshModal(false);
      } else if (data.type === REFRESH_EVENTS.LOGOUT) {
        logout();
        if (typeof window !== "undefined") {
          window.location.href = window.location.origin;
        }
      } else if (data.type === REFRESH_EVENTS.IDLE_WARNING) {
        if (isAuthenticated && !showIdleModal) {
          setShowIdleModal(true);
          setIdleModalSource("tab");
          setIdleWarningOrigin("remote");
        }
      } else if (data.type === REFRESH_EVENTS.IDLE_DECISION) {
        if (data.action === "logout") {
          setShowIdleModal(false);
          setIdleModalSource(null);
          hasResetOnFocusRef.current = false;
          performLogoutRef.current();
        } else if (data.action === "proceed") {
          setShowIdleModal(false);
          setIdleModalSource(null);
          hasResetOnFocusRef.current = false;
          resetTabIdle();
          resetSystemIdle();
        }
      }
    },
  });

  useEffect(() => {
    broadcastRef.current = broadcast;
  }, [broadcast]);

  useEffect(() => {
    performLogoutRef.current = performLogout;
  }, [performLogout]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const dismissAndProceed = () => {
      if (idleWarningOrigin !== "self") return;
      if (!showIdleModal) return;

      setShowIdleModal(false);
      setIdleModalSource(null);
      setIdleWarningOrigin("self");
      hasResetOnFocusRef.current = false;
      broadcastRef.current({
        type: REFRESH_EVENTS.IDLE_DECISION,
        action: "proceed",
      });
    };

    const handlePageHide = () => {
      dismissAndProceed();
    };

    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [showIdleModal, idleWarningOrigin]);

  const armRefreshTimer = useCallback(
    (currentToken) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      const remaining = jwtExpiresInMs(currentToken);
      if (remaining == null) return;

      const ms = Math.max(remaining - REFRESH_LEAD_MS, MIN_TIMER_MS);

      refreshTimerRef.current = setTimeout(() => {
        if (!silent) {
          setShowRefreshModal(true);
        } else {
          performRefresh().catch(() => {
            performLogout();
          });
        }
      }, ms);
    },
    [silent, performRefresh, performLogout],
  );

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    armRefreshTimer(token);
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [token, isAuthenticated, silent, armRefreshTimer]);

  useEffect(() => {
    try {
      localStorage.setItem(SESSION_HANDLED_FLAG, "1");
    } catch {
    void 0;
    }
    return () => {
      try {
        localStorage.removeItem(SESSION_HANDLED_FLAG);
      } catch {
      void 0;
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.__cfSessionRefresh = performRefresh;
    return () => {
      if (window.__cfSessionRefresh === performRefresh) {
        delete window.__cfSessionRefresh;
      }
    };
  }, [performRefresh]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.__cfSessionRefreshModalOpen = showRefreshModal || showIdleModal;
  }, [showRefreshModal, showIdleModal]);

  const handleExtend = useCallback(() => {
    setShowRefreshModal(false);
    performRefresh().catch(() => {
      performLogout();
    });
  }, [performRefresh, performLogout]);

  const handleIdleProceed = useCallback(() => {
    setShowIdleModal(false);
    setIdleModalSource(null);
    setIdleWarningOrigin("self");
    hasResetOnFocusRef.current = false;
    resetTabIdle();
    resetSystemIdle();
    broadcastRef.current({
      type: REFRESH_EVENTS.IDLE_DECISION,
      action: "proceed",
    });
  }, [resetTabIdle, resetSystemIdle]);

  const handleIdleLogout = useCallback(() => {
    setShowIdleModal(false);
    setIdleModalSource(null);
    setIdleWarningOrigin("self");
    hasResetOnFocusRef.current = false;
    broadcastRef.current({
      type: REFRESH_EVENTS.IDLE_DECISION,
      action: "logout",
    });
    performLogout();
  }, [performLogout]);

  const contextValue = useMemo(
    () => ({
      refreshToken: performRefresh,
      performLogout,
      isSilentRefreshRoute,
    }),
    [performRefresh, performLogout],
  );

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
      <IdleModal
        open={showIdleModal}
        source={idleWarningOrigin}
        countdown={countdown}
        onProceed={handleIdleProceed}
        onLogout={handleIdleLogout}
      />
      <SessionExpiryModal
        open={showRefreshModal}
        countdown={countdown}
        onExtend={handleExtend}
        onLogout={performLogout}
      />
    </SessionContext.Provider>
  );
};